import { useRef, useEffect } from "react";
import { IonModal, IonContent } from "@ionic/react";
import { useSelector } from "react-redux";
import { actions } from "../../Redux/store";
import { motion } from "framer-motion";
import type { Variants } from "framer-motion";
import bookCall from "../../assets/imgs/book-call-bg.png";
import { hapticsImpactLight } from "../../lib/utils";
import { useMixpanel } from "../../Context/MixpanelContext/MixpanelProvider";

export default function BookCallModal() {
  const isOpen = useSelector((state: any) => state.modal.bookCallModal?.open);
  const modal = useRef<HTMLIonModalElement>(null);
  const hasTrackedModalShown = useRef(false);
  const Mixpanel = useMixpanel();

  // Track modal shown event when it opens (only once per modal open)
  useEffect(() => {
    if (isOpen && !hasTrackedModalShown.current) {
      Mixpanel.trackEvent("book_call_modal_shown", {
        name: "Book Call Modal Shown",
      });
      hasTrackedModalShown.current = true;
    } else if (!isOpen) {
      // Reset the tracking flag when modal closes
      hasTrackedModalShown.current = false;
    }
  }, [isOpen, Mixpanel]);

  // Animation variants for content
  const contentVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
  };

  // Animation variants for button section
  const buttonVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.3,
        ease: "easeOut",
      },
    },
  };

  function dismiss() {
    modal.current?.dismiss();
    actions.modal.closeBookCallModal(null);
  }

  const handleBookCall = async () => {
    try {
      hapticsImpactLight();
      Mixpanel.trackEvent("book_call_selected", {
        name: "Book Call Selected",
      });
      window.open(
        "https://calendly.com/team-iru/user-discovery-meetings",
        "_system",
      );
      actions.modal.closeBookCallModal(null);
    } catch (error) {
      console.error("Failed to open Calendly link:", error);
    }
  };

  const later = () => {
    hapticsImpactLight();
    dismiss();
    Mixpanel.trackEvent("book_call_later_selected", {
      name: "Book Call Later Selected",
    });
  };

  return (
    <IonModal
      expandToScroll={false}
      onDidDismiss={dismiss}
      className="league-selection-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      initialBreakpoint={0.55}
      breakpoints={[0, 0.55]}
      ref={modal}
      handle={false}
    >
      <div className="flex flex-col h-full">
        <IonContent scrollY className="flex-1 overflow-y-auto">
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate={isOpen ? "visible" : "hidden"}
          >
            <div>
              <img src={bookCall} alt="bookCall" />
            </div>
            <div className="p-3">
              <div className="flex-col items-center justify-center w-full text-center  gap-1 ">
                <p className="text-[24px] text-white font-semibold">
                  Thanks for Subscribing
                </p>
                <p className="text-[12px] text-white/70 font-medium">
                  Got a minute to shape what we build next?
                </p>
              </div>

              <div className=" mt-6 text-center">
                <p className="text-[12px] text-white/80 font-normal">
                  Want to share feedback or tell us what features you’d like to
                  see? Book a quick call with our team — your input helps shape
                  the future of the app.
                </p>
              </div>
            </div>
          </motion.div>
        </IonContent>

        <motion.div
          className="pt-[10px] bg-[#000513] flex-col items-center justify-center px-8"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)",
          }}
          variants={buttonVariants}
          initial="hidden"
          animate={isOpen ? "visible" : "hidden"}
        >
          <button
            className="w-full h-[48px] flex justify-center items-center bg-[#00E0FF]  rounded-[42px] "
            onClick={handleBookCall}
          >
            <span className="text-[12px] font-medium text-black">
              Book a Quick Call
            </span>
          </button>

          <div className="text-center mt-3" onClick={later}>
            <span className="text-[12px] text-white font-normal">Later</span>
          </div>
        </motion.div>
      </div>
    </IonModal>
  );
}
